import { BrowserRouter as Router } from 'react-router-dom'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import config from './config'
import { updateToken, restoreSession, clearSession, sessionRestorationComplete, selectIsAuthenticated, selectIsRestoringSession } from './store/authUserSlice'
import store from './store'
import AppRoutes from './routes'
import { ToastProvider } from './context/ToastContext'
import { NetworkProvider } from './context/NetworkContext'
import { LayoutProvider } from './context/LayoutContext'
import { isPublicPath } from './constants/publicPaths'
import { clearSavedEntries } from './web/utils/offlineDB'
import './App.css'

function App() {
    const dispatch = useDispatch()
    const isAuthenticated = useSelector(selectIsAuthenticated)
    const isRestoringSession = useSelector(selectIsRestoringSession)

    useEffect(() => {
        clearSavedEntries().catch(console.error);
    }, []);

    useEffect(() => {
        // SECURITY: On app mount, attempt to restore session using httpOnly refresh cookie
        // This replaces the old localStorage token restoration
        const restoreAuthSession = async () => {
            try {
                // Get current path to check if we're on a public page
                const currentPath = window.location.pathname
                const isPublicPage = isPublicPath(currentPath)

                // First, restore non-sensitive user data from localStorage
                dispatch(restoreSession())

                // Get current state AFTER restoreSession dispatch
                // Check if we have session data (userId indicates previous session existed)
                const state = store.getState()
                const hasSessionData = !!state.authUser?.userInfo?.userId

                // Only attempt refresh if we have session data AND not on a public page
                // IMPORTANT: Don't attempt refresh on signin page to prevent auto-login after logout
                if (hasSessionData && !isPublicPage && !isAuthenticated) {
                    try {
                        const response = await axios.post(
                            `${config.apiUrl}/refresh/`,
                            {}, // No body needed, refresh_token is in httpOnly cookie
                            {
                                withCredentials: true,
                                // Fail fast to avoid blocking the whole app behind the boot loader.
                                // (Default browser/network timeouts can be ~30s+ on bad connections.)
                                timeout: 8000,
                            }
                        )

                        if (response.data.token) {
                            // Store the new access token in Redux state (memory only)
                            dispatch(updateToken(response.data.token))
                        }
                    } catch (error) {
                        // Refresh token invalid/expired/not present, or request timed out.
                        dispatch(clearSession())
                    }
                }
            } finally {
                // Mark session restoration as complete (always)
                // This allows PrivateRoute to start checking for authentication
                dispatch(sessionRestorationComplete())
            }
        }

        restoreAuthSession()
    }, [dispatch, isAuthenticated])

    // Show loading screen while restoring session
    // This prevents race condition where PrivateRoute checks before token is restored
    // Skip the loading screen on public paths — no need to block /signin behind session restoration
    if (isRestoringSession && !isPublicPath(window.location.pathname)) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontSize: '18px',
                color: '#666'
            }}>
                Loading...
            </div>
        )
    }

    return (
        <Router basename={process.env.PUBLIC_URL}>
            <ToastProvider>
                <NetworkProvider>
                    <LayoutProvider>
                        <AppRoutes />
                    </LayoutProvider>
                </NetworkProvider>
            </ToastProvider>
        </Router>
    )
}

export default App
